/**
 * Row actions of the participants grid, as plain data. One list feeds both the ⋮ button and the
 * right-click menu, so they can never drift apart. Items are hidden (not disabled) when they do
 * not apply to the participant's current state or the user's permissions.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  DollarSign,
  Eye,
  FileText,
  Link2,
  PenLine,
  Printer,
  QrCode,
  Receipt,
  RefreshCw,
  RotateCcw,
  ShieldCheck,
  Trash2,
  Undo2,
  UserCheck,
  Vote,
} from 'lucide-react';
import type { ParticipantWithDetails } from '@/Services/eventParticipants.service';
import { needsSignature } from '../public/registrationEditLogic';

export interface ParticipantAction {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Runs on select. Mutually exclusive with `href`. */
  onSelect?: () => void;
  /** Opens in a new tab */
  href?: string;
  destructive?: boolean;
}

export interface ParticipantActionGroup {
  id: string;
  /** Section title, shown only in menus long enough to need it */
  label: string;
  actions: ParticipantAction[];
}

/** What the payments list says about one participant's receipts */
export interface ParticipantReceiptInfo {
  /** First recorded payment that still has no receipt */
  paymentWithoutReceipt?: { paymentId: string; amount: number; method: string | null };
  /** Latest issued receipt */
  receiptId?: string;
}

export interface ParticipantActionHandlers {
  onReview: (p: ParticipantWithDetails) => void;
  onRequestPayment: (p: ParticipantWithDetails) => void;
  onRequestReversal: (p: ParticipantWithDetails) => void;
  onIssueReceipt: (p: ParticipantWithDetails, payment: NonNullable<ParticipantReceiptInfo['paymentWithoutReceipt']>) => void;
  onViewReceipt: (receiptId: string) => void;
  onCheckIn: (p: ParticipantWithDetails) => void;
  onMarkAbsent: (p: ParticipantWithDetails) => void;
  onResetAttendance: (p: ParticipantWithDetails) => void;
  onPrintSticker: (p: ParticipantWithDetails) => void;
  onEditForm: (p: ParticipantWithDetails) => void;
  onEditLink: (p: ParticipantWithDetails) => void;
  onToggleMandatory?: (p: ParticipantWithDetails) => void;
  /** Sets "Habilitado para votar" (event_participant.register) */
  onSetVoting?: (p: ParticipantWithDetails, enabled: boolean) => void;
  onRecalculatePrice: (id: string) => void;
  onDelete: (id: string) => void;
}

export interface ParticipantActionContext {
  handlers: ParticipantActionHandlers;
  /** Payments + attendance allowed (event OPEN or CLOSED) */
  canOperate: boolean;
  canIssueReceipts: boolean;
  /** event_participant.register: edit the form / generate the participant's edit link */
  canEditForm: boolean;
  /** Event signature_mode: a Digital event with an unsigned form asks for the signature */
  signatureMode?: string | null;
  isAdmin: boolean;
  receiptInfo?: ParticipantReceiptInfo;
}

/**
 * Whether the participant can vote, as far as the participant itself says. null means the
 * default: ministers and delegates vote, plain guests do not (an election may change the guest
 * default; the explicit switch always wins).
 */
export function isVotingEnabled(p: Pick<ParticipantWithDetails, 'voting_enabled' | 'minister_id' | 'guest'>): boolean {
  return p.voting_enabled ?? (!!p.minister_id || !!p.guest?.is_delegate);
}

export function buildParticipantActions(
  p: ParticipantWithDetails,
  ctx: ParticipantActionContext,
): ParticipantActionGroup[] {
  const { handlers: h, canOperate, canIssueReceipts, canEditForm, signatureMode, isAdmin, receiptInfo } = ctx;
  const groups: ParticipantActionGroup[] = [];

  groups.push({
    id: 'view',
    label: 'Registro',
    actions: [{ id: 'detail', label: 'Ver detalle', icon: Eye, onSelect: () => h.onReview(p) }],
  });

  const payment: ParticipantAction[] = [];
  if (canOperate) {
    if (p.payment_status === 'PENDING' || p.payment_status === 'REVERSED') {
      payment.push({ id: 'charge', label: 'Cobrar', icon: DollarSign, onSelect: () => h.onRequestPayment(p) });
    }
    if (p.payment_status === 'PARTIAL') {
      payment.push({ id: 'charge-balance', label: 'Registrar pago del saldo', icon: DollarSign, onSelect: () => h.onRequestPayment(p) });
    }
  }
  const missing = receiptInfo?.paymentWithoutReceipt;
  if (canIssueReceipts && missing) {
    payment.push({ id: 'issue-receipt', label: 'Emitir recibo', icon: Receipt, onSelect: () => h.onIssueReceipt(p, missing) });
  }
  if (receiptInfo?.receiptId) {
    const receiptId = receiptInfo.receiptId;
    payment.push({ id: 'view-receipt', label: 'Ver recibo', icon: FileText, onSelect: () => h.onViewReceipt(receiptId) });
  }
  if (canOperate && (p.payment_status === 'PAID' || p.payment_status === 'PARTIAL')) {
    payment.push({ id: 'reverse', label: 'Revertir pago', icon: Undo2, onSelect: () => h.onRequestReversal(p) });
  }
  if (payment.length) groups.push({ id: 'payment', label: 'Cobro', actions: payment });

  if (canOperate) {
    const attendance: ParticipantAction[] = [];
    if (p.attendance_status !== 'ATTENDED') {
      attendance.push({
        id: 'check-in',
        label: p.attendance_status === 'ABSENT' ? 'Marcar como presente' : 'Marcar asistencia',
        icon: UserCheck,
        onSelect: () => h.onCheckIn(p),
      });
    }
    if (p.attendance_status !== 'ABSENT') {
      attendance.push({ id: 'absent', label: 'Marcar como ausente', icon: Ban, onSelect: () => h.onMarkAbsent(p) });
    }
    if (p.attendance_status !== 'PENDING') {
      attendance.push({ id: 'reset-attendance', label: 'Reiniciar asistencia', icon: RotateCcw, onSelect: () => h.onResetAttendance(p) });
    }
    groups.push({ id: 'attendance', label: 'Asistencia', actions: attendance });
  }

  groups.push({
    id: 'print',
    label: 'Imprimir',
    actions: [
      { id: 'sticker', label: 'Imprimir sticker', icon: QrCode, onSelect: () => h.onPrintSticker(p) },
      { id: 'form', label: 'Imprimir formulario de inscripción', icon: Printer, href: `/p/${p.public_token}` },
    ],
  });

  if (canEditForm) {
    const unsigned = needsSignature(p, signatureMode);
    groups.push({
      id: 'form',
      label: 'Formulario',
      actions: [
        { id: 'edit-form', label: 'Editar formulario', icon: PenLine, onSelect: () => h.onEditForm(p) },
        {
          id: 'edit-link',
          label: unsigned ? 'Pedir firma (enlace)' : 'Enlace de edición',
          icon: Link2,
          onSelect: () => h.onEditLink(p),
        },
      ],
    });
  }

  const manage: ParticipantAction[] = [];
  if (h.onToggleMandatory) {
    const toggle = h.onToggleMandatory;
    manage.push({
      id: 'mandatory',
      label: p.is_mandatory ? 'Quitar obligatorio' : 'Marcar obligatorio',
      icon: ShieldCheck,
      onSelect: () => toggle(p),
    });
  }
  if (h.onSetVoting) {
    const setVoting = h.onSetVoting;
    const enabled = isVotingEnabled(p);
    manage.push({
      id: 'voting',
      label: enabled ? 'Quitar habilitación para votar' : 'Habilitar para votar',
      icon: Vote,
      onSelect: () => setVoting(p, !enabled),
    });
  }
  manage.push({ id: 'recalculate', label: 'Recalcular precio', icon: RefreshCw, onSelect: () => h.onRecalculatePrice(p.id) });
  groups.push({ id: 'manage', label: 'Gestión', actions: manage });

  if (isAdmin) {
    groups.push({
      id: 'danger',
      label: 'Eliminar',
      actions: [{ id: 'delete', label: 'Eliminar participante', icon: Trash2, destructive: true, onSelect: () => h.onDelete(p.id) }],
    });
  }

  return groups;
}

